import { useEffect, useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload, User, Mail, Phone, Briefcase, Lock, Shield, Building, ShieldOff, MailCheck, MonitorSmartphone, Users as UsersIcon, KeyRound } from "lucide-react";
import { PasswordField, passwordValid } from "@/components/PasswordField";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const roles = [{ v: "admin", l: "Administrator" }, { v: "projektleder", l: "Projektleder" }, { v: "medarbejder", l: "Medarbejder" }];
const roleLabel = Object.fromEntries(roles.map((r) => [r.v, r.l]));
const DEPARTMENTS = [{ v: "salg", l: "Salg" }, { v: "indkoeb", l: "Indkøb" }, { v: "kvalitet", l: "Kvalitet" }, { v: "produktion", l: "Produktion" }];
const deptLabel = Object.fromEntries(DEPARTMENTS.map((d) => [d.v, d.l]));
const deptBadge = {
  salg: "bg-[#F9F1D9] text-[#8A6E17] border-[#ECE0B6]",
  indkoeb: "bg-[#F4F0EA] text-[#75654F] border-[#E4DBCF]",
  kvalitet: "bg-[#E7EADF] text-[#7E856C] border-[#D3D8C6]",
  produktion: "bg-[#F4EFEC] text-[#806B61] border-[#E6DDD6]",
};
const roleBadge = {
  admin: "bg-[#F1DEDA] text-[#A83232] border-[#E9C9C3]",
  projektleder: "bg-[#F9F1D9] text-[#8A6E17] border-[#ECE0B6]",
  medarbejder: "bg-[#F3F1EC] text-stone-600 border-[#EBE8E3]",
};

const initials = (name) => (name || "").split(" ").filter(Boolean).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
function UserAvatar({ name, avatarUrl, size = 36, testid }) {
  const src = avatarUrl ? `${BACKEND}${avatarUrl}` : null;
  const style = { width: size, height: size };
  return src ? (
    <img src={src} alt={name} data-testid={testid} className="rounded-full object-cover border border-[#EBE8E3] shrink-0" style={style} />
  ) : (
    <div data-testid={testid} className="rounded-full bg-[#F3F1EC] text-stone-500 flex items-center justify-center font-semibold shrink-0" style={{ ...style, fontSize: size * 0.36 }}>{initials(name) || "?"}</div>
  );
}

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const EMPTY = { name: "", email: "", password: "", role: "projektleder", departments: ["salg"], title: "", phone: "", active: true, account_type: "personal" };
  const [f, setF] = useState(EMPTY);
  const [toDelete, setToDelete] = useState(null);
  const [pwTarget, setPwTarget] = useState(null);      // bruger der skal have sat adgangskode (fælleskonto)
  const [pw, setPw] = useState(""); const [pw2, setPw2] = useState(""); const [pwBusy, setPwBusy] = useState(false);
  const [confirmPersonal, setConfirmPersonal] = useState(null); // {u, payload}
  const [editAvatarUrl, setEditAvatarUrl] = useState(null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setEditAvatarUrl(null); setF(EMPTY); setOpen(true); };
  const openEdit = (u) => { setEditing(u.id); setEditAvatarUrl(u.avatar_url || null); setF({ name: u.name, email: u.email, password: "", role: u.role, departments: u.departments && u.departments.length ? u.departments : ["salg"], title: u.title || "", phone: u.phone || "", active: u.active !== false, account_type: u.account_type === "shared" ? "shared" : "personal" }); setOpen(true); };
  const isShared = f.account_type === "shared";
  const setType = (t) => setF((p) => ({ ...p, account_type: t, role: t === "shared" && p.role === "admin" ? "medarbejder" : p.role }));
  const toggleDept = (v) => setF((p) => ({ ...p, departments: p.departments.includes(v) ? p.departments.filter((d) => d !== v) : [...p.departments, v] }));

  const onAvatarSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !editing) return;
    if (!file.type.startsWith("image/")) return toast.error("Filen skal være et billede");
    setAvatarBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post(`/users/${editing}/avatar`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditAvatarUrl(r.data.avatar_url);
      toast.success("Profilbillede opdateret");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setAvatarBusy(false); }
  };

  const removeAvatar = async () => {
    if (!editing) return;
    setAvatarBusy(true);
    try { await api.delete(`/users/${editing}/avatar`); setEditAvatarUrl(null); toast.success("Profilbillede fjernet"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setAvatarBusy(false); }
  };

  const doUpdate = async (payload) => {
    const r = await api.put(`/users/${editing}`, payload);
    toast.success("Bruger opdateret");
    setOpen(false); load();
    if (r.data?.needs_shared_password) { setPw(""); setPw2(""); setPwTarget({ ...r.data, justConverted: true }); }
  };
  const save = async () => {
    if (!f.departments || f.departments.length === 0) return toast.error("Vælg mindst én afdeling");
    if (isShared && f.role === "admin") return toast.error("En fælleskonto kan ikke være administrator");
    try {
      if (editing) {
        const before = users.find((x) => x.id === editing);
        const payload = { name: f.name, role: f.role, departments: f.departments, active: f.active, title: f.title, phone: f.phone, account_type: f.account_type };
        if ((before?.account_type === "shared") && f.account_type === "personal") { setConfirmPersonal({ u: before, payload }); return; }
        await doUpdate(payload);
      } else {
        if (isShared) {
          if (!passwordValid(f.password)) return toast.error("Adgangskoden opfylder ikke kravene");
          await api.post("/users", { name: f.name, email: f.email, role: f.role, departments: f.departments, title: f.title, phone: f.phone, account_type: "shared", password: f.password });
          toast.success("Fælleskonto oprettet");
        } else {
          await api.post("/users", { name: f.name, email: f.email, role: f.role, departments: f.departments, title: f.title, phone: f.phone, account_type: "personal" });
          toast.success("Bruger oprettet – invitation sendt pr. e-mail");
        }
        setOpen(false); load();
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const confirmToPersonal = async () => {
    const { payload } = confirmPersonal; setConfirmPersonal(null);
    try { await doUpdate(payload); toast.success("Invitation sendt – den fælles adgangskode virker ikke længere"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const submitSharedPassword = async () => {
    if (!passwordValid(pw)) return toast.error("Adgangskoden opfylder ikke kravene");
    if (pw !== pw2) return toast.error("De to adgangskoder er ikke ens");
    setPwBusy(true);
    try { await api.post(`/users/${pwTarget.id}/set-password`, { password: pw }); toast.success(`Adgangskode sat – ${pwTarget.name} er logget ud på alle enheder`); setPwTarget(null); setPw(""); setPw2(""); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPwBusy(false); }
  };

  const logoutUserAll = async (u) => {
    try { await api.post(`/users/${u.id}/logout-all`); toast.success(`${u.name} er logget ud på alle enheder`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const resendInvite = async (u) => {
    try { await api.post(`/users/${u.id}/invite`); toast.success(`Invitation sendt igen til ${u.email}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const sendReset = async (u) => {
    try { await api.post(`/users/${u.id}/send-reset`); toast.success(`Link til nulstilling sendt til ${u.email}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const trustNextLogin = async (u) => {
    try { await api.post(`/users/${u.id}/trust-next-login`); toast.success(`${u.name}s næste login springer e-mailkoden over (gyldigt 15 min)`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleActive = async (u) => {
    try { await api.put(`/users/${u.id}`, { active: !(u.active !== false) }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/users/${toDelete.id}`);
      toast.success("Bruger slettet");
      setToDelete(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setToDelete(null); }
  };

  return (
    <div className="rise-in">
      <div className="flex items-end justify-between mb-8">
        <div><h1 className="text-4xl sm:text-5xl font-light tracking-tight text-[#1A1919]">Brugere & roller</h1></div>
        <Button onClick={openNew} className="rounded-xl font-medium bg-[#A83232] hover:bg-[#8f2a2a]" data-testid="new-user-btn"><Plus className="h-4 w-4 mr-2" /> Ny bruger</Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {users.map((u) => (
          <div key={u.id} className="pcard pcard-hover p-6 flex flex-col" data-testid={`user-row-${u.id}`}>
            <div className="flex items-start gap-3">
              <UserAvatar name={u.name} avatarUrl={u.avatar_url} size={44} testid={`user-avatar-${u.id}`} />
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-[#1A1919] truncate">{u.name}{u.id === me.id && <span className="text-xs text-stone-400 font-normal"> (dig)</span>}</p>
                {u.account_type === "shared" && <span className="inline-flex items-center gap-1 mt-0.5 px-2 py-0.5 rounded-full text-[11px] font-medium border bg-[#EEF0F4] text-[#4F5B73] border-[#D9DEE8]" data-testid={`user-shared-badge-${u.id}`}><UsersIcon className="h-3 w-3" /> Fælleskonto</span>}
                <p className="text-sm text-stone-500 truncate" data-testid={`user-title-${u.id}`}>{u.title || "—"}</p>
              </div>
              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border shrink-0 ${roleBadge[u.role] || roleBadge.medarbejder}`}>{roleLabel[u.role] || u.role}</span>
            </div>
            {u.invite_status === "invited" && u.account_type !== "shared" && (
              <div className="mt-3 flex items-center justify-between gap-2 rounded-xl border border-[#EADFB8] bg-[#FBF6E4] px-3 py-2" data-testid={`user-invited-${u.id}`}>
                <span className="text-xs font-medium text-[#8A6D1F] inline-flex items-center gap-1.5"><MailCheck className="h-3.5 w-3.5" /> Inviteret – ikke aktiveret</span>
                <button onClick={() => resendInvite(u)} className="text-xs font-semibold text-[#A83232] hover:underline" data-testid={`resend-invite-${u.id}`}>Send invitation igen</button>
              </div>
            )}

            <div className="mt-4 space-y-2 flex-1 text-sm">
              <p className="flex items-center gap-2 text-stone-600 truncate"><Mail className="h-3.5 w-3.5 text-stone-400 shrink-0" strokeWidth={1.75} /> {u.email}</p>
              <p className="flex items-center gap-2 text-stone-600" data-testid={`user-phone-${u.id}`}><Phone className="h-3.5 w-3.5 text-stone-400 shrink-0" strokeWidth={1.75} /> {u.phone || "—"}</p>
              <div className="flex flex-wrap items-center gap-1.5 pt-1" data-testid={`user-departments-${u.id}`}>
                <Building className="h-3.5 w-3.5 text-stone-400 shrink-0" strokeWidth={1.75} />
                {(u.departments && u.departments.length ? u.departments : []).map((d) => (
                  <span key={d} className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${deptBadge[d] || "bg-[#F3F1EC] text-stone-500 border-[#EBE8E3]"}`}>{deptLabel[d] || d}</span>
                ))}
                {(!u.departments || u.departments.length === 0) && <span className="text-xs text-stone-400">Ingen afdeling</span>}
              </div>
            </div>

            <div className="flex items-center justify-between mt-5 pt-4 border-t border-[#EBE8E3]">
              <label className="flex items-center gap-2 text-xs text-stone-500 cursor-pointer select-none">
                <Switch checked={u.active !== false} onCheckedChange={() => toggleActive(u)} data-testid={`toggle-user-${u.id}`} /> {u.active !== false ? "Aktiv" : "Inaktiv"}
              </label>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="rounded-xl border-[#EBE8E3]" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}><Pencil className="h-3.5 w-3.5 mr-1.5" /> Rediger</Button>
                {u.account_type === "shared" ? (
                  <Button size="icon" variant="ghost" title="Sæt adgangskode (fælleskonto)" className="rounded-xl h-9 w-9 text-stone-400 hover:text-stone-900" onClick={() => { setPw(""); setPw2(""); setPwTarget(u); }} data-testid={`set-shared-password-${u.id}`}><KeyRound className="h-3.5 w-3.5" /></Button>
                ) : (
                  <Button size="icon" variant="ghost" title="Nødprocedure: betro næste login (15 min) – bruges hvis e-mailkoden ikke kan leveres" className="rounded-xl h-9 w-9 text-stone-400 hover:text-stone-900" onClick={() => trustNextLogin(u)} data-testid={`trust-next-login-${u.id}`}><MonitorSmartphone className="h-3.5 w-3.5" /></Button>
                )}
                <Button size="icon" variant="ghost" title="Log bruger ud overalt" className="rounded-xl h-9 w-9 text-stone-400 hover:text-stone-900" onClick={() => logoutUserAll(u)} data-testid={`logout-all-user-${u.id}`}><ShieldOff className="h-3.5 w-3.5" /></Button>
                <Button size="icon" variant="ghost" className="rounded-xl h-9 w-9 text-stone-400 hover:text-destructive hover:bg-[#F1DEDA]" onClick={() => setToDelete(u)} data-testid={`delete-user-${u.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl sm:max-w-[560px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <span className="h-10 w-10 rounded-xl bg-[#F1DEDA] text-[#A83232] flex items-center justify-center shrink-0"><User className="h-5 w-5" strokeWidth={1.75} /></span>
              <div>
                <DialogTitle className="text-xl font-semibold leading-tight">{editing ? "Rediger bruger" : "Ny bruger"}</DialogTitle>
                <DialogDescription className="text-sm text-stone-500">Angiv brugerens oplysninger og rolle.</DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <div className="space-y-5 mt-1">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Navn</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none" strokeWidth={1.75} />
                <Input data-testid="user-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="rounded-xl h-11 pl-9 bg-white" placeholder="Fx Mathias Hylleberg" />
              </div>
            </div>
            {editing && (
              <div className="space-y-1.5 rounded-2xl border border-[#EBE8E3] bg-[#FBFAF8] p-3.5">
                <Label className="text-sm font-medium">Profilbillede</Label>
                <div className="flex items-center gap-4">
                  <UserAvatar name={f.name} avatarUrl={editAvatarUrl} size={64} testid="edit-user-avatar" />
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onAvatarSelected} data-testid="avatar-file-input" />
                  <Button variant="outline" className="rounded-xl border-[#EBE8E3] bg-white" onClick={() => fileRef.current?.click()} disabled={avatarBusy} data-testid="upload-avatar-btn"><Upload className="h-4 w-4 mr-2" strokeWidth={1.75} /> {avatarBusy ? "Uploader…" : "Upload billede"}</Button>
                  {editAvatarUrl && <Button variant="ghost" className="rounded-xl text-destructive hover:text-destructive hover:bg-destructive/10" onClick={removeAvatar} disabled={avatarBusy} data-testid="remove-avatar-btn">Fjern</Button>}
                </div>
                <p className="text-xs text-stone-400">Vises på delingssiden ved siden af den projektansvarlige.</p>
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none" strokeWidth={1.75} />
                <Input data-testid="user-email" type="email" value={f.email} disabled={!!editing} onChange={(e) => setF({ ...f, email: e.target.value })} className="rounded-xl h-11 pl-9 bg-white disabled:bg-[#F3F1EC]" placeholder="navn@bsm.dk" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Titel</Label>
                <div className="relative">
                  <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none" strokeWidth={1.75} />
                  <Input data-testid="user-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="rounded-xl h-11 pl-9 bg-white" placeholder="Fx Projektleder" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Telefon</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none" strokeWidth={1.75} />
                  <Input data-testid="user-phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="rounded-xl h-11 pl-9 bg-white" placeholder="+45 12 34 56 78" />
                </div>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Kontotype</Label>
              <div className="grid grid-cols-2 gap-2" data-testid="user-account-type">
                {[{ v: "personal", l: "Personlig", I: User }, { v: "shared", l: "Fælleskonto (uden e-mail)", I: UsersIcon }].map(({ v, l, I }) => (
                  <button type="button" key={v} onClick={() => setType(v)} data-testid={`user-account-type-${v}`}
                    className={`flex items-center justify-center gap-2 rounded-xl h-11 text-sm font-medium border [transition-property:color,background-color,border-color] duration-200 ${f.account_type === v ? "bg-[#F1DEDA] text-[#A83232] border-[#E9C9C3]" : "bg-white text-stone-500 border-[#EBE8E3] hover:border-[#A83232]/40"}`}>
                    <I className="h-4 w-4" strokeWidth={1.75} /> {l}
                  </button>
                ))}
              </div>
              {isShared && <p className="text-xs text-stone-500" data-testid="shared-help">Ingen loginkode, ingen betroede enheder, ingen mails. Administrator sætter adgangskoden. Kan ikke være administrator.</p>}
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Adgangskode</Label>
              {editing && isShared ? (
                <div className="flex items-center justify-between gap-3 rounded-xl border border-[#EBE8E3] bg-[#FBFAF8] px-3.5 py-3">
                  <p className="text-xs text-stone-500 flex items-center gap-2"><Lock className="h-3.5 w-3.5 text-stone-400" strokeWidth={1.75} /> Fælleskonto: administrator sætter adgangskoden. Kontoen logges ud overalt ved skift.</p>
                  <Button type="button" size="sm" variant="outline" className="rounded-xl border-[#EBE8E3] bg-white shrink-0" onClick={() => { setPw(""); setPw2(""); setPwTarget(users.find((x) => x.id === editing)); }} data-testid="set-shared-password-btn">Sæt adgangskode</Button>
                </div>
              ) : editing ? (
                <div className="flex items-center justify-between gap-3 rounded-xl border border-[#EBE8E3] bg-[#FBFAF8] px-3.5 py-3">
                  <p className="text-xs text-stone-500 flex items-center gap-2"><Lock className="h-3.5 w-3.5 text-stone-400" strokeWidth={1.75} /> Administrator kan ikke se eller sætte adgangskoder.</p>
                  <Button type="button" size="sm" variant="outline" className="rounded-xl border-[#EBE8E3] bg-white shrink-0" onClick={() => sendReset(users.find((x) => x.id === editing))} data-testid="send-reset-link-btn">Send link til nulstilling</Button>
                </div>
              ) : isShared ? (
                <div className="space-y-1.5"><PasswordField value={f.password} onChange={(v) => setF({ ...f, password: v })} testid="shared-new-password" /></div>
              ) : (
                <p className="text-xs text-stone-500 rounded-xl border border-[#EBE8E3] bg-[#FBFAF8] px-3.5 py-3 flex items-center gap-2" data-testid="invite-info"><Lock className="h-3.5 w-3.5 text-stone-400" strokeWidth={1.75} /> Brugeren modtager en invitation pr. e-mail og vælger selv sin adgangskode (linket gælder 24 timer).</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Rolle</Label>
              <div className="relative">
                <Shield className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none z-10" strokeWidth={1.75} />
                <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
                  <SelectTrigger data-testid="user-role" className="rounded-xl h-11 pl-9 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>{roles.map((r) => <SelectItem key={r.v} value={r.v} disabled={isShared && r.v === "admin"}>{r.l}{isShared && r.v === "admin" ? " – ikke for fælleskonti" : ""}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Afdeling(er)</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="user-departments-select">
                {DEPARTMENTS.map((d) => {
                  const on = f.departments.includes(d.v);
                  return (
                    <button type="button" key={d.v} onClick={() => toggleDept(d.v)} data-testid={`user-dept-${d.v}`}
                      className={`flex items-center justify-center gap-2 rounded-xl h-11 text-sm font-medium border [transition-property:color,background-color,border-color] duration-200 ${on ? "bg-[#F1DEDA] text-[#A83232] border-[#E9C9C3]" : "bg-white text-stone-500 border-[#EBE8E3] hover:border-[#A83232]/40"}`}>
                      <Building className="h-4 w-4" strokeWidth={1.75} /> {d.l}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-stone-400">Vælg mindst én. Administrator ser alt uanset afdeling.</p>
            </div>
            {editing && (
              <Button variant="ghost" className="rounded-xl text-destructive hover:text-destructive hover:bg-destructive/10 px-0" onClick={() => { const u = users.find((x) => x.id === editing); setOpen(false); setToDelete(u); }} data-testid="delete-user-in-dialog">
                <Trash2 className="h-4 w-4 mr-2" /> Slet denne bruger
              </Button>
            )}
          </div>
          <DialogFooter className="mt-2">
            <Button variant="outline" className="rounded-xl border-[#EBE8E3]" onClick={() => setOpen(false)}>Annullér</Button>
            <Button className="rounded-xl font-medium bg-[#A83232] hover:bg-[#8f2a2a]" onClick={save} data-testid="save-user-btn">Gem</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwTarget} onOpenChange={(o) => { if (!o) { setPwTarget(null); setPw(""); setPw2(""); } }}>
        <DialogContent className="rounded-2xl sm:max-w-[460px]" data-testid="shared-password-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">Sæt adgangskode – {pwTarget?.name}</DialogTitle>
            <DialogDescription className="text-sm text-stone-500">{pwTarget?.justConverted ? "Kontoen er nu en fælleskonto og er logget ud overalt. Sæt en ny adgangskode, så den kan bruges igen." : "Fælleskonto. Kontoen logges ud på alle enheder, når adgangskoden ændres."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-1">
            <div className="space-y-1.5"><Label className="text-sm font-medium">Ny adgangskode</Label><PasswordField value={pw} onChange={setPw} testid="shared-pw" /></div>
            <div className="space-y-1.5"><Label className="text-sm font-medium">Gentag adgangskode</Label><PasswordField value={pw2} onChange={setPw2} placeholder="Gentag adgangskode" testid="shared-pw2" showRules={false} /></div>
          </div>
          <DialogFooter className="mt-2">
            <Button variant="outline" className="rounded-xl border-[#EBE8E3]" onClick={() => setPwTarget(null)}>Annullér</Button>
            <Button className="rounded-xl font-medium bg-[#A83232] hover:bg-[#8f2a2a]" onClick={submitSharedPassword} disabled={pwBusy || !pw} data-testid="shared-pw-submit">{pwBusy ? "Gemmer…" : "Sæt adgangskode"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmPersonal} onOpenChange={(o) => !o && setConfirmPersonal(null)}>
        <AlertDialogContent data-testid="confirm-personal-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl font-medium">Skift til personlig konto</AlertDialogTitle>
            <AlertDialogDescription>Den fælles adgangskode stopper med at virke, og der sendes en invitation til {confirmPersonal?.u?.email}. Fortsæt?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Annullér</AlertDialogCancel>
            <AlertDialogAction className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]" onClick={confirmToPersonal} data-testid="confirm-personal-btn">Fortsæt</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl font-medium">Slet bruger</AlertDialogTitle>
            <AlertDialogDescription>Er du sikker på, at du vil slette denne bruger? Denne handling kan ikke fortrydes.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Annullér</AlertDialogCancel>
            <AlertDialogAction className="rounded-xl bg-destructive hover:bg-destructive/90" onClick={confirmDelete} data-testid="confirm-delete-user">Slet bruger</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
